import { Story } from "@/components/builder/stories/context";
import { useStoriesListContext } from "@/components/builder/stories/list/context";
import { buildStoryUrl, extractStories } from "@/components/builder/stories/list/helper";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { Builder } from "@builder.io/react";

interface GridModeInput {
  index: number;
  gridMode: GridMode;
}

interface ComponentProps {
  initialOffset?: number;
  numberOfStories?: number;
  gridModes?: GridModeInput[];
}

interface ModeClasses {
  articleClassNames: string;
  imageClassNames: string;
}

type GridMode = "1:1" | "2:1" | "2:2";
const GridModeToClassesMap: { [mode: string]: ModeClasses } = {
  "1:1": {
    articleClassNames: "",
    imageClassNames: "aspect-[4/5]",
  },
  "2:1": {
    articleClassNames: "col-span-2",
    imageClassNames: "aspect-2",
  },
  "2:2": {
    articleClassNames: "col-span-2 row-span-2",
    imageClassNames: "aspect-[4/5]",
  },
};

function CreateGridModeMap(gridModeInputs: GridModeInput[]): GridMode[] {
  if (gridModeInputs.length === 0) {
    return ["1:1"];
  }

  const gridModeMap: GridMode[] = [];

  for (
    let i = 0;
    i <
    Math.max.apply(
      Math,
      gridModeInputs.map((input) => input.index),
    );
    i++
  ) {
    gridModeMap.push("1:1");
  }
  for (let i = 0; i < gridModeInputs.length; i++) {
    const gridModeInput = gridModeInputs[i];
    gridModeMap[gridModeInput.index] = gridModeInput.gridMode;
  }
  return gridModeMap;
}

export default function StoriesGrid(props: ComponentProps) {
  const storiesContext = useStoriesListContext();

  const initialOffset = props.initialOffset || 0;
  const numberOfStories = props.numberOfStories;
  const storiesMap = CreateGridModeMap(props.gridModes || []);

  function getStoryClasses(index: number) {
    const storiesMapIndex = index % storiesMap.length;
    return GridModeToClassesMap[storiesMap[storiesMapIndex]];
  }

  const stories = extractStories(Array.from(storiesContext.stories), initialOffset, numberOfStories);
  if (stories.length === 0 && Builder.isEditing) {
    return (
      <p className="text-center text-lg leading-8 text-gray-600">No stories found, but message is shown in editor</p>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {stories.map((story, index) => (
          <StoryElement
            key={story.id}
            story={story}
            index={index}
            selectGenre={(genre) => storiesContext.selectGenres([genre])}
            getStoryClasses={getStoryClasses}
          />
        ))}
      </div>
    </div>
  );
}

interface StoryComponentProps {
  story: Story;
  index: number;
  selectGenre: (genre: string) => void;
  getStoryClasses: (index: number) => ModeClasses;
}

function StoryElement({ story, index, selectGenre, getStoryClasses }: StoryComponentProps) {
  return (
    <article key={story.id} className={cn("group overflow-hidden bg-white", getStoryClasses(index).articleClassNames)}>
      <div className={cn("w-full overflow-hidden", getStoryClasses(index).imageClassNames)}>
        <TranslatedLink href={buildStoryUrl(story)} className={cn("relative w-full")}>
          <img
            src={story.data.previewImage}
            alt=""
            className={cn(
              "w-full object-cover",
              "grayscale-0 group-hover:scale-125 group-hover:grayscale",
              "transition-all duration-1000",
              getStoryClasses(index).imageClassNames,
            )}
          />
        </TranslatedLink>
      </div>
      {story.data.genres && story.data.genres.length > 0 && (
        <div className="my-3.5 flex flex-row flex-wrap items-start gap-2">
          {story.data.genres.map((genre) => (
            <div
              key={genre}
              onClick={() => selectGenre(genre)}
              className={cn(
                "cursor-pointer",
                "bg-black text-xs font-medium uppercase text-white",
                "rounded-full px-2.5 py-0.5",
                "transition duration-300 hover:bg-gray-600",
              )}
            >
              {genre}
            </div>
          ))}
        </div>
      )}
      <TranslatedLink href={buildStoryUrl(story)} className="line-clamp-4 text-2xl group-hover:underline">
        {story.data.title}
      </TranslatedLink>
    </article>
  );
}
